import { useEffect } from 'react';

import {
	ANALYTICS_SOURCE,
	BYLINE_SSR_SAMPLES_ACTION_SUBJECT,
	BYLINE_SSR_SAMPLES_ACTION_SUBJECT_ID,
} from '../analytics.ts';
import { useTwgAnalyticsEvents } from '../useTwgAnalyticsEvents.ts';
import {
	getBylineSsrSampleAttributes,
	type BylineSsrSampleAttributes,
} from '../utils/getBylineSsrSampleAttributes.ts';

export const getBylineSsrSamplesMeasuredEvent = (
	attributes: BylineSsrSampleAttributes,
): {
	readonly type: 'sendOperationalEvent';
	readonly data: {
		readonly action: 'measured';
		readonly actionSubject: 'bylineSsrSamples';
		readonly actionSubjectId: 'teamworkGraphBylineSsrSamples';
		readonly source: 'byline-connections';
		readonly attributes: BylineSsrSampleAttributes;
	};
} =>
	({
		type: 'sendOperationalEvent',
		data: {
			action: 'measured',
			actionSubject: BYLINE_SSR_SAMPLES_ACTION_SUBJECT,
			actionSubjectId: BYLINE_SSR_SAMPLES_ACTION_SUBJECT_ID,
			source: ANALYTICS_SOURCE,
			attributes,
		},
	}) as const;

// One report per document: the measures object is shipped once with the server-rendered page, and
// a second mounted byline must not report it again.
const reportedSsrMeasures = new WeakSet<object>();

/**
 * Fires `bylineSsrSamples measured` once on a server-rendered load: each byline sample's outcome
 * and query time, so the SSR guard's timeout rate and latency can be watched. It fires even when
 * SSR hid the byline, since a timed-out sample is the usual reason it was hidden.
 */
export const useBylineSsrSampleAnalytics = (isSsrLoad: boolean): void => {
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();

	useEffect(() => {
		const ssrMeasures: unknown = window.__SSR_MEASURES__;
		if (
			!isSsrLoad ||
			typeof ssrMeasures !== 'object' ||
			ssrMeasures === null ||
			reportedSsrMeasures.has(ssrMeasures)
		) {
			return;
		}
		reportedSsrMeasures.add(ssrMeasures);
		const attributes = getBylineSsrSampleAttributes(ssrMeasures);
		if (attributes) {
			createAnalyticsEvent(getBylineSsrSamplesMeasuredEvent(attributes)).fire();
		}
	}, [isSsrLoad, createAnalyticsEvent]);
};
